import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { ChevronLeft } from 'lucide-react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getCheckpointSummary,
  getEnrichedCheckpointResults,
  groupByLoop,
  formatTime,
  formatPace,
  formatSpeed,
  CheckpointSummary,
  EnrichedCheckpointResult,
  LoopAggregate
} from '../services/checkpointResultsService';

type TranslateFn = (key: string, options?: any) => string;

const formatDistance = (distance: number | null | undefined, t: TranslateFn): string => {
  if (distance === null || distance === undefined) {
    return t('runners.profile.kutc.table.na');
  }
  if (Number.isFinite(distance)) {
    return `${distance.toFixed(1)} km`;
  }
  return t('runners.profile.kutc.table.na');
};

const formatAscent = (ascent: number | null | undefined, t: TranslateFn): string => {
  if (ascent === null || ascent === undefined) {
    return t('runners.profile.kutc.table.na');
  }
  return `${ascent.toFixed(0)} m`;
};

const formatPosition = (position: number | null | undefined): string => {
  if (!position || position <= 0) {
    return '—';
  }
  return `#${position}`;
};

const RunnerCheckpointAnalysisPage: React.FC = () => {
  const { personId, editionId } = useParams<{ personId: string; editionId: string }>();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<CheckpointSummary | null>(null);
  const [loops, setLoops] = useState<LoopAggregate[]>([]);
  const [checkpoints, setCheckpoints] = useState<EnrichedCheckpointResult[]>([]);
  const [runnerRouteId, setRunnerRouteId] = useState<string | null>(null);

  useEffect(() => {
    if (!personId || !editionId) {
      setError(t('runners.kutcAnalysis.errors.missingInfo'));
      setLoading(false);
      return;
    }

    const parsedPersonId = Number(personId);
    if (!Number.isFinite(parsedPersonId)) {
      setError(t('runners.kutcAnalysis.errors.invalidRunnerId'));
      setLoading(false);
      return;
    }

    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        setRunnerRouteId(String(parsedPersonId));

        const enriched = await getEnrichedCheckpointResults(editionId, parsedPersonId);
        if (!mounted) return;
        setCheckpoints(enriched);

        const [loopAggregates, summaryData] = await Promise.all([
          groupByLoop(enriched, editionId),
          getCheckpointSummary(editionId, parsedPersonId)
        ]);
        if (!mounted) return;
        setLoops(loopAggregates);
        setSummary(summaryData);
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.message || t('runners.kutcAnalysis.errors.loadFailed'));
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [editionId, personId, t]);

  const checkpointsByLoop = useMemo(() => {
    const map = new Map<number, EnrichedCheckpointResult[]>();
    checkpoints.forEach((cp) => {
      if (!map.has(cp.loopNumber)) {
        map.set(cp.loopNumber, []);
      }
      map.get(cp.loopNumber)!.push(cp);
    });
    return map;
  }, [checkpoints]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!summary) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="info">{t('runners.kutcAnalysis.info.noCheckpointData')}</Alert>
      </Container>
    );
  }

  const editionLabel = summary.eventEditionId?.toUpperCase?.() || editionId?.toUpperCase?.() || editionId;
  const runnerProfileUrl = `/runners/${runnerRouteId || personId}`;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          component={RouterLink}
          to={runnerProfileUrl}
          startIcon={<ChevronLeft />}
          variant="text"
          sx={{ mb: 1 }}
        >
          {t('runners.kutcAnalysis.backToProfile')}
        </Button>

        <Breadcrumbs aria-label={t('common.breadcrumb')}>
          <MuiLink component={RouterLink} color="inherit" to="/runners/search">
            {t('runners.search.title')}
          </MuiLink>
          <MuiLink component={RouterLink} color="inherit" to={runnerProfileUrl}>
            {summary.participantName}
          </MuiLink>
          <Typography color="text.primary">{editionLabel}</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom>
          {summary.participantName}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {editionLabel} • {summary.raceName}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                {t('runners.kutcAnalysis.summary.bib')}
              </Typography>
              <Typography variant="h5">{summary.bib || '—'}</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                {t('runners.kutcAnalysis.summary.loopsCompleted')}
              </Typography>
              <Typography variant="h5">{summary.totalLoops}</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                {t('runners.kutcAnalysis.summary.totalDistance')}
              </Typography>
              <Typography variant="h6">{formatDistance(summary.totalDistance, t)}</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                {t('runners.kutcAnalysis.summary.totalAscent')}
              </Typography>
              <Typography variant="h6">{formatAscent(summary.totalAscent, t)}</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                {t('runners.kutcAnalysis.summary.totalRaceTime')}
              </Typography>
              <Typography variant="h6">{summary.totalRaceTimeFormatted}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('runners.kutcAnalysis.summary.movingRest', {
                  moving: summary.totalMovingTimeFormatted,
                  rest: summary.totalRestTimeFormatted
                })}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.secondary">
                {t('runners.kutcAnalysis.summary.averagePaceSpeed')}
              </Typography>
              <Typography variant="h6">{summary.averagePaceFormatted}</Typography>
              <Typography variant="body2" color="text.secondary">
                {summary.averageSpeedKmh ? formatSpeed(summary.averageSpeedKmh) : '—'}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('runners.kutcAnalysis.performanceInsights')}
        </Typography>
        <Grid container spacing={2}>
          {summary.fastestLeg && (
            <Grid item xs={12} md={6} lg={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="success.main">
                    {t('runners.kutcAnalysis.cards.fastestLeg')}
                  </Typography>
                  <Typography variant="body2">
                    {summary.fastestLeg.checkpointName} ({t('events.loop')} {summary.fastestLeg.loopNumber})
                  </Typography>
                  <Typography variant="h6">{summary.fastestLeg.paceFormatted}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          {summary.slowestLeg && (
            <Grid item xs={12} md={6} lg={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="warning.main">
                    {t('runners.kutcAnalysis.cards.slowestLeg')}
                  </Typography>
                  <Typography variant="body2">
                    {summary.slowestLeg.checkpointName} ({t('events.loop')} {summary.slowestLeg.loopNumber})
                  </Typography>
                  <Typography variant="h6">{summary.slowestLeg.paceFormatted}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          {summary.biggestGain && (
            <Grid item xs={12} md={6} lg={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="info.main">
                    {t('runners.kutcAnalysis.cards.biggestGain')}
                  </Typography>
                  <Typography variant="body2">
                    {summary.biggestGain.checkpointName} ({t('events.loop')} {summary.biggestGain.loopNumber})
                  </Typography>
                  <Typography variant="h6">
                    +{summary.biggestGain.positionChange}{' '}
                    {t('runners.kutcAnalysis.places', { count: Math.abs(summary.biggestGain.positionChange) })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.biggestGain.type === 'race'
                      ? t('runners.kutcAnalysis.racePosition')
                      : t('runners.kutcAnalysis.overallPosition')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          {summary.biggestLoss && (
            <Grid item xs={12} md={6} lg={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="error.main">
                    {t('runners.kutcAnalysis.cards.biggestLoss')}
                  </Typography>
                  <Typography variant="body2">
                    {summary.biggestLoss.checkpointName} ({t('events.loop')} {summary.biggestLoss.loopNumber})
                  </Typography>
                  <Typography variant="h6">
                    {summary.biggestLoss.positionChange}{' '}
                    {t('runners.kutcAnalysis.places', { count: Math.abs(summary.biggestLoss.positionChange) })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.biggestLoss.type === 'race'
                      ? t('runners.kutcAnalysis.racePosition')
                      : t('runners.kutcAnalysis.overallPosition')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('runners.kutcAnalysis.loopBreakdown')}
        </Typography>
        <Stack spacing={3}>
          {loops.map((loop) => {
            const loopCheckpoints = checkpointsByLoop.get(loop.loopNumber) || [];
            const raceChange = loop.loopNumber > 1 ? loop.racePositionChangeThisLoop : null;
            const overallChange = loop.loopNumber > 1 ? loop.overallPositionChangeThisLoop : null;
            return (
              <Paper key={loop.loopNumber} sx={{ p: 3 }}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h6">
                    {t('events.loop')} {loop.loopNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('runners.kutcAnalysis.loop.distanceAscent', {
                      distance: formatDistance(loop.cumulativeDistance, t),
                      ascent: formatAscent(loop.cumulativeAscent, t)
                    })}
                  </Typography>
                </Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2">
                      <strong>{t('runners.kutcAnalysis.loop.totalTime')}:</strong> {loop.totalLoopTimeFormatted}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('runners.kutcAnalysis.loop.movingTime')}:</strong> {loop.movingTimeFormatted}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('runners.kutcAnalysis.loop.restTime')}:</strong> {loop.restTimeFormatted}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('runners.kutcAnalysis.loop.accRest')}:</strong> {loop.accumulatedRestTimeFormatted}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2">
                      <strong>{t('runners.kutcAnalysis.loop.avgPace')}:</strong> {formatPace(loop.averagePaceMinPerKm)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('runners.kutcAnalysis.loop.avgSpeed')}:</strong> {loop.averageSpeedKmh ? formatSpeed(loop.averageSpeedKmh) : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2">
                      <strong>{t('runners.kutcAnalysis.racePosition')}:</strong> {formatPosition(loop.racePositionAtEnd)}
                      {raceChange != null && (
                        <> ({raceChange > 0 ? '+' : ''}{raceChange})</>
                      )}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('runners.kutcAnalysis.overallPosition')}:</strong> {formatPosition(loop.overallPositionAtEnd)}
                      {overallChange != null && (
                        <> ({overallChange > 0 ? '+' : ''}{overallChange})</>
                      )}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('runners.kutcAnalysis.loop.checkpoints')}:</strong> {loopCheckpoints.length}
                    </Typography>
                  </Grid>
                </Grid>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('runners.kutcAnalysis.table.checkpoint')}</TableCell>
                        <TableCell>{t('runners.kutcAnalysis.table.time')}</TableCell>
                        <TableCell>{t('runners.kutcAnalysis.table.legTime')}</TableCell>
                        <TableCell>{t('runners.kutcAnalysis.table.legPace')}</TableCell>
                        <TableCell>{t('runners.kutcAnalysis.table.racePosition')}</TableCell>
                        <TableCell>{t('runners.kutcAnalysis.table.overallPosition')}</TableCell>
                        <TableCell align="right">{t('runners.kutcAnalysis.table.restSeconds')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loopCheckpoints.map((cp) => (
                        <TableRow key={cp.sequenceNumber}>
                          <TableCell>
                            <Typography variant="body2">{cp.checkpointName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {cp.scanTimeFormatted}
                            </Typography>
                          </TableCell>
                          <TableCell>{cp.raceTimeFormatted}</TableCell>
                          <TableCell>{cp.legTimeFormatted}</TableCell>
                          <TableCell>{cp.legPaceFormatted}</TableCell>
                          <TableCell>{formatPosition(cp.racePosition)}</TableCell>
                          <TableCell>{formatPosition(cp.overallPosition)}</TableCell>
                          <TableCell align="right">{formatTime(cp.restTimeSeconds ?? null)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })}
        </Stack>
      </Box>
    </Container>
  );
};

export default RunnerCheckpointAnalysisPage;
